import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import { Zap, ChevronDown, User, Settings, LogOut, BookOpen, LayoutDashboard, BarChart2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";


export default function TopNav({ projectId, projectTitle }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [balance, setBalance] = useState(user?.credits_balance || 0);
  const [showUserMenu, setShowUserMenu] = useState(false);

  useEffect(() => {
    if (user) {
      axios.get(`${API}/credits/balance`, { withCredentials: true })
        .then(r => setBalance(r.data.balance))
        .catch(() => setBalance(user?.credits_balance || 0));
    }
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate("/auth");
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + "/");

  return (
    <nav
      className="h-14 flex items-center justify-between px-6 border-b sticky top-0 z-50"
      style={{ background: "var(--surface-page)", borderColor: "var(--border-subtle)" }}
    >
      {/* Left: Logo */}
      <div className="flex items-center gap-6">
        <Link to="/studio" className="flex items-center gap-2 no-underline">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center font-black text-sm"
            style={{ background: "linear-gradient(135deg, var(--accent-gold), var(--accent-gold))" }}>
            A
          </div>
          <span className="font-display font-semibold tracking-tight text-sm hidden sm:block">ArcScript AI</span>
        </Link>

        <div className="flex items-center gap-1">
          <Link
            to="/studio"
            data-testid="nav-studio"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors no-underline ${isActive("/studio") ? "font-medium text-[var(--ink-primary)] bg-[var(--surface-card)] border-b-2 border-[var(--accent-gold)]" : "text-[var(--ink-secondary)] hover:text-[var(--ink-primary)] hover:bg-[var(--surface-hover)]"}`}
          >
            <LayoutDashboard size={14} />
            Studio
          </Link>
          <Link
            to="/templates"
            data-testid="nav-templates"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors no-underline ${isActive("/templates") ? "font-medium text-[var(--ink-primary)] bg-[var(--surface-card)] border-b-2 border-[var(--accent-gold)]" : "text-[var(--ink-secondary)] hover:text-[var(--ink-primary)] hover:bg-[var(--surface-hover)]"}`}
          >
            <BookOpen size={14} />
            Templates
          </Link>
        </div>
      </div>

      {/* Right */}
      <div className="flex items-center gap-3">
        {/* Credits */}
        <div
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm"
          style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)" }}
          data-testid="credit-balance"
        >
          <Zap size={13} className="text-[var(--semantic-warning)]" />
          <span className="font-semibold">{balance.toLocaleString()}</span>
        </div>

        {/* User menu */}
        <div className="relative">
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            data-testid="user-menu-button"
            className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors"
            style={{ background: "var(--surface-card)", border: "1px solid var(--border-subtle)" }}
          >
            <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
              style={{ background: "var(--accent-gold)" }}>
              {(user?.name || user?.email || "U")[0].toUpperCase()}
            </div>
            <span className="text-[var(--ink-secondary)] hidden sm:block max-w-[120px] truncate">{user?.name || user?.email}</span>
            <ChevronDown size={12} className="text-[var(--ink-tertiary)]" />
          </button>

          {showUserMenu && (
            <div
              className="absolute right-0 top-full mt-1 w-48 rounded-xl overflow-hidden shadow-2xl z-50"
              style={{ background: "var(--surface-raised)", border: "1px solid var(--border-subtle)" }}
            >
              <div className="px-4 py-3 border-b" style={{ borderColor: "var(--border-subtle)" }}>
                <div className="text-sm font-medium truncate">{user?.name}</div>
                <div className="text-[var(--ink-tertiary)] text-xs truncate">{user?.email}</div>
                <div className="text-[var(--accent-gold)] text-xs mt-0.5 capitalize">{user?.subscription_tier} tier</div>
              </div>
              <Link
                to="/settings"
                className="flex items-center gap-3 px-4 py-2.5 text-[var(--ink-secondary)] hover: hover:bg-[var(--surface-card)] transition-colors no-underline text-sm"
                onClick={() => setShowUserMenu(false)}
                data-testid="nav-settings"
              >
                <Settings size={14} />
                Settings
              </Link>
              {user?.email === "admin@arcscript.ai" && (
                <Link
                  to="/admin/analytics"
                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-[var(--surface-card)] transition-colors no-underline text-sm"
                  style={{ color: "var(--accent-gold)" }}
                  onClick={() => setShowUserMenu(false)}
                  data-testid="nav-analytics"
                >
                  <BarChart2 size={14} />
                  Analytics
                </Link>
              )}
              <button
                onClick={handleLogout}
                data-testid="nav-logout"
                className="w-full flex items-center gap-3 px-4 py-2.5 text-[var(--semantic-error)] hover:bg-[var(--surface-card)] transition-colors text-sm text-left"
              >
                <LogOut size={14} />
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>

      {showUserMenu && (
        <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)} />
      )}
    </nav>
  );
}

